import { useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';

import DROPDOWN_ICON from '../../../../assets/images/dropdown.svg';

// Words the report abbreviates, written the way people write them.
const ACRONYMS = {
    acpi: 'ACPI', amt: 'AMT', bios: 'BIOS', bmc: 'BMC', cpu: 'CPU', dash: 'DASH', dma: 'DMA', edid: 'EDID', gb: 'GB', gpu: 'GPU',
    hdd: 'HDD', id: 'ID', iommu: 'IOMMU', ip: 'IP', ipmi: 'IPMI', ipv4: 'IPv4', ipv6: 'IPv6', kvm: 'KVM', lan: 'LAN', mac: 'MAC',
    mb: 'MB', mbps: 'Mbps', me: 'ME', mhz: 'MHz', nvme: 'NVMe', os: 'OS', pci: 'PCI', ram: 'RAM', sku: 'SKU', smbios: 'SMBIOS',
    ssd: 'SSD', tpm: 'TPM', uart: 'UART', usb: 'USB', uuid: 'UUID', wifi: 'Wi-Fi',
};

// "physicalCores" -> "Physical Cores", "pciAnomalies" -> "PCI Anomalies", "macAddress" -> "MAC Address"
export const humanizeKey = (key) => String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => ACRONYMS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const GENERAL_SECTION = '__general__';
const MAX_TABLE_COLUMNS = 10;
const MUTED = '#98A2B3';

const isPrimitive = (value) => value === null || value === undefined || typeof value !== 'object';
const isEmptyValue = (value) => value === null || value === undefined || value === '';

// How many values a section holds, for the section list.
const countFields = (value) => {
    if (isPrimitive(value)) return 1;
    if (Array.isArray(value)) return value.length;
    return Object.keys(value).length;
};

const BooleanChip = ({ value }) => (
    <Box component='span' sx={{
        display: 'inline-block', px: 1, py: 0.1, borderRadius: 10, fontSize: 12, fontWeight: 600,
        color: value ? '#067647' : '#475467', bgcolor: value ? '#ECFDF3' : '#F2F4F7', border: '1px solid', borderColor: value ? '#ABEFC6' : '#EAECF0',
    }}>
        {value ? 'Yes' : 'No'}
    </Box>
);

const PrimitiveValue = ({ value }) => {
    if (isEmptyValue(value)) return <Box component='span' sx={{ color: MUTED }}>—</Box>;
    if (typeof value === 'boolean') return <BooleanChip value={value} />;
    return <Box component='span' sx={{ wordBreak: 'break-word' }}>{String(value)}</Box>;
};

// A short one-line form of a nested value, for table cells.
const compact = (value) => {
    if (isPrimitive(value)) return value;
    if (Array.isArray(value)) {
        if (!value.length) return '';
        return value.every(isPrimitive) ? value.join(', ') : `${value.length} items`;
    }
    const entries = Object.entries(value).filter(([, v]) => isPrimitive(v) && !isEmptyValue(v));
    return entries.map(([k, v]) => `${humanizeKey(k)}: ${typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v}`).join(' · ');
};

// Primitive fields as a two-column label / value grid.
const FieldGrid = ({ entries }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 3, rowGap: 1.25 }}>
        {entries.map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', minWidth: 0, borderBottom: '1px dashed', borderColor: 'divider', pb: 0.75 }}>
                <Typography variant='body2' sx={{ color: 'text.secondary', flex: '0 0 42%', minWidth: 0, wordBreak: 'break-word' }}>{humanizeKey(key)}</Typography>
                <Typography component='div' variant='body2' sx={{ fontWeight: 500, minWidth: 0, flex: 1 }}><PrimitiveValue value={value} /></Typography>
            </Box>
        ))}
    </Box>
);

// A list of similar objects (disks, PCI devices, interfaces...) as a table.
const ObjectTable = ({ items }) => {
    const columns = [...new Set(items.flatMap((item) => Object.keys(item)))].slice(0, MAX_TABLE_COLUMNS);
    return (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: '100%' }}>
            <Table size='small'>
                <TableHead>
                    <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 36 }}>#</TableCell>
                        {columns.map((column) => (
                            <TableCell key={column} sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>{humanizeKey(column)}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item, index) => (
                        <TableRow key={index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{ color: MUTED }}>{index + 1}</TableCell>
                            {columns.map((column) => (
                                <TableCell key={column} sx={{ verticalAlign: 'top', maxWidth: 320 }}>
                                    <PrimitiveValue value={compact(item[column])} />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// Any report value laid out by its shape: fields as a grid, nested parts as titled blocks, lists
// of objects as tables, lists of words as chips.
const ValueView = ({ value, depth = 0 }) => {
    if (isPrimitive(value)) {
        return <Typography component='div' variant='body2'><PrimitiveValue value={value} /></Typography>;
    }
    if (Array.isArray(value)) {
        if (!value.length) return <Typography variant='body2' sx={{ color: MUTED }}>None</Typography>;
        if (value.every(isPrimitive)) {
            return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {value.map((item, index) => (
                        <Box key={index} component='span' sx={{ px: 1, py: 0.25, borderRadius: 1.5, bgcolor: 'primary.50', color: 'primary.800', fontSize: 12.5 }}>{String(item)}</Box>
                    ))}
                </Box>
            );
        }
        if (value.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
            return <ObjectTable items={value} />;
        }
        return value.map((item, index) => <ValueView key={index} value={item} depth={depth + 1} />);
    }

    const entries = Object.entries(value);
    if (!entries.length) return <Typography variant='body2' sx={{ color: MUTED }}>No data</Typography>;
    const fields = entries.filter(([, v]) => isPrimitive(v));
    const nested = entries.filter(([, v]) => !isPrimitive(v));
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {fields.length > 0 && <FieldGrid entries={fields} />}
            {nested.map(([key, child]) => (
                <Box key={key} sx={{ pl: 1.5, borderLeft: '3px solid', borderColor: depth === 0 ? 'primary.100' : 'divider' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13.5, mb: 1, color: depth === 0 ? 'primary.main' : 'text.primary' }}>
                        {humanizeKey(key)}
                        {Array.isArray(child) && <Box component='span' sx={{ color: MUTED, fontWeight: 400, ml: 0.75 }}>({child.length})</Box>}
                    </Typography>
                    <ValueView value={child} depth={depth + 1} />
                </Box>
            ))}
        </Box>
    );
};

// The device's full hardware report: one collapsible card per section, with a search box and
// expand / collapse all. The first section starts open.
export const ReportViewer = ({ hardware }) => {
    const sections = useMemo(() => {
        const entries = Object.entries(hardware ?? {});
        const general = entries.filter(([, value]) => isPrimitive(value));
        return [
            ...(general.length ? [{ key: GENERAL_SECTION, name: 'General', value: Object.fromEntries(general) }] : []),
            ...entries.filter(([, value]) => !isPrimitive(value)).map(([key, value]) => ({ key, name: humanizeKey(key), value })),
        ];
    }, [hardware]);
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(() => new Set(sections.slice(0, 1).map((section) => section.key)));

    useEffect(() => {
        setExpanded(new Set(sections.slice(0, 1).map((section) => section.key)));
        setSearch('');
    }, [sections]);

    const shown = sections.filter((section) => section.name.toLowerCase().includes(search.trim().toLowerCase()));
    const toggle = (key) => setExpanded((open) => {
        const next = new Set(open);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <TextField size='small' placeholder='Search sections' value={search} onChange={(event) => setSearch(event.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position='start'>🔍</InputAdornment> }} sx={{ flex: '1 1 240px', maxWidth: 360 }} />
                <Typography variant='caption' sx={{ color: MUTED }}>{shown.length} of {sections.length} sections</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    <Button size='small' sx={{ textTransform: 'none', fontWeight: 600 }} onClick={() => setExpanded(new Set(shown.map((section) => section.key)))}>Expand all</Button>
                    <Button size='small' sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }} onClick={() => setExpanded(new Set())}>Collapse all</Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {shown.map((section) => {
                    const isOpen = expanded.has(section.key);
                    return (
                        <Accordion key={section.key} expanded={isOpen} onChange={() => toggle(section.key)} disableGutters elevation={0}
                            TransitionProps={{ unmountOnExit: true }}
                            sx={{
                                border: '1px solid', borderColor: isOpen ? 'primary.100' : 'divider', borderRadius: '12px !important',
                                overflow: 'hidden', transition: 'border-color .2s, box-shadow .2s', '&::before': { display: 'none' },
                                ...(isOpen ? { boxShadow: '0 4px 14px rgba(63, 65, 209, 0.08)' } : { '&:hover': { borderColor: 'primary.100' } }),
                            }}>
                            <AccordionSummary expandIcon={<img src={DROPDOWN_ICON} alt='' />}
                                sx={{ px: 2, minHeight: 52, bgcolor: isOpen ? 'primary.50' : 'background.base', '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, my: 1.25 } }}>
                                <Box component='span' sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isOpen ? 'primary.main' : 'primary.100', flex: 'none' }} />
                                <Typography sx={{ fontWeight: 600, color: isOpen ? 'primary.main' : 'text.primary' }}>{section.name}</Typography>
                                <Box component='span' sx={{ ml: 'auto', mr: 1, px: 1, py: 0.1, borderRadius: 10, fontSize: 12, color: 'text.secondary', bgcolor: '#F2F4F7' }}>
                                    {countFields(section.value)}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2.5, pt: 2, pb: 2.5, borderTop: '1px solid', borderColor: 'primary.50' }}>
                                <ValueView value={section.value} />
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
                {!shown.length && <Typography variant='body2' sx={{ color: MUTED, textAlign: 'center', py: 4 }}>No section matches.</Typography>}
            </Box>
        </Box>
    );
};
